import React, { useContext, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AuthContext } from '../context/auth/authState'
import { NotificationContext, LanguageContext } from '../context'

function SignupModal({ showModal, onClose, type, prefillData, onComplete }) {

  const { signInWithEmail, signUpWithEmail, signInWithMethod, resetPassword } = useContext(AuthContext)
  const { toast } = useContext(NotificationContext)
  const { t, localizedDocumentUrl } = useContext(LanguageContext)

  const [modalType, setModalType] = useState(type)
  const [loading, setLoading] = useState(false)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [terms, setTerms] = useState(false)

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    // clear password fields when switching between login and signup
    setPassword('')
    setPasswordConfirmation('')
  }, [modalType])

  useEffect(() => {
    // prefill email if available
    if (prefillData?.email) {
      setEmail(prefillData.email)
    }
  }, [prefillData])

  useEffect(() => {
    setModalType(type)
  }, [type])

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/
    const valid = re.test(email)
    if (!valid) {
      toast({ message: t('auth.invalid_email'), variant: 'error' })
    }
    return valid
  }

  const validatePassword = (password) => {
    // password must be at least 8 characters long and contain at least one special character
    if (password.length < 8) {
      toast({ message: t('auth.password_short'), variant: 'error' })
      return false
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password)) {
      toast({ message: t('auth.password_character'), variant: 'error' })
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (modalType === 'reset') {
      setLoading(true);
      const response = await resetPassword(email);
      setLoading(false);
      if (response.error) {
        toast({ message: response.error.message, variant: 'error', timeout: 8000 })
      } else {
        toast({ message: t('auth.reset_sent'), variant: 'success', timeout: 8000 })
        setModalType('login')
      }
      return
    }
    if (modalType === 'signup') {
      if (!validateEmail(email)) {
        return
      }
      if (!validatePassword(password)) {
        return
      }
      if (password !== passwordConfirmation) {
        toast({ message: t('auth.password_mismatch'), variant: 'error' })
        return
      }
      if (!terms) {
        toast({ message: t('auth.accept_cgu'), variant: 'error' })
        return
      }
      setLoading(true);
      const response = await signUpWithEmail(email, password);
      if (response) {
        if (onComplete) {
          await onComplete()
        }
        setLoading(false)
        onClose()
      } else {
        setLoading(false)
      }
    } else if (modalType === 'login') {
      setLoading(true);
      const response = await signInWithEmail(email, password);
      // setLoading(false);

      if (response) {
        if (onComplete) {
          await onComplete()
        }
        setLoading(false)
        onClose()
      } else {
        setLoading(false)
      }
    }
  }

  const handleSignInWithMethod = (method) => async () => {
    setLoading(true)
    const response = await signInWithMethod(method)
    if (response) {
      if (onComplete) {
        await onComplete()
        setLoading(false)
      }
      onClose()
    } else {
      setLoading(false)
    }
  }

  const handlePromptResetPassword = (e) => {
    e.preventDefault()
    setModalType('reset')
  }

  return (
    <div className={`w-full ${showModal ? '' : 'hidden'}`}>
      <div
        className="justify-center items-center z-50 rounded-lg modal-container auth-modal">
        {modalType === 'signup' && <>
          <div className="flex items-start justify-between pt-16 pb-9 px-5  ">

            <h3 className="modal-title">
              {t('auth.signup_title')}&nbsp;
              <br className="md:hidden"/>
              {t('auth.signup_title_line2')}
            </h3>
            <button
              className="p-1 bg-transparent text-black text-3xl absolute top-2 right-4"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          {/*body*/}
          <div className="modal-box">
            <form onSubmit={handleSubmit}>
              <div className='mb-6'>
                <label className='block  mb-2'>
                  {`Email`}*
                </label>
                <input type='text' className='border' name='email' autoComplete='email' value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className='mb-6'>
                <label className='block  mb-2'>
                  {t('auth.password_label')}*
                </label>
                <div className='flex gap-1'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className='border'
                    name='password'
                    autoComplete='password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className='flex justify-center items-center !border !border-gray-400 rounded-lg' onClick={togglePasswordVisibility}>
                    <button
                      type='button'
                      className='!bg-transparent h-full'
                    >
                      <img src={showPassword ? '/eye.svg' : '/eye-slash.svg'} className='w-6' />
                    </button>
                  </div>
                </div>
                <p className='text-xs mt-2'>{t('auth.password_instructions')}</p>
              </div>
              <div className='mb-6'>
                <label className='block  mb-2'>
                  {t('auth.password_confirm_label')}*
                </label>
                <div className='flex gap-1'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className='border'
                    name='passwordConfirmation'
                    autoComplete='password'
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                  />
                  <div className='flex justify-center items-center !border !border-gray-400 rounded-lg' onClick={togglePasswordVisibility}>
                    <button
                      type='button'
                      className='!bg-transparent h-full'
                    >
                      <img src={showPassword ? '/eye.svg' : '/eye-slash.svg'} className='w-6' />
                    </button>
                  </div>
                </div>
              </div>

              <div className='mb-6 custom-checkbox'>
                <div className='flex gap-5 relative'>
                  <input type='checkbox' name='terms' id='condition' checked={terms} onChange={(e) => setTerms(e.target.checked)} />
                  <label className='block mb-2 condition' htmlFor='condition'>
                    {`${t('auth.accept_label1')} `}
                    <Link to={localizedDocumentUrl('application')} target='_blank' className='link'>{t('auth.accept_label_cgu')}</Link>
                    {` ${t('auth.accept_label2')} `}
                    <Link to={localizedDocumentUrl('privacy')} target='_blank' className='link'>{t('auth.accept_label_pp')}</Link>
                    {` ${t('auth.accept_label3')}`}
                  </label>
                </div>
              </div>
              <button type='submit'
                className={`mid w-full btn-loading mb-6 ${loading ? 'running' : ''}`}
                disabled={loading}>
                <div className="loading"></div>
                {t('auth.signup_button')}
              </button>
            </form>
            <p>
              {t('auth.already_have_account')} <a className='link !underline !font-semibold' onClick={() => setModalType('login')}>{t('auth.connection')}</a>
            </p>
            <div className='separator-line'>
              <span>{t('generic.or')}</span>
            </div>
            <ul>
              <li>
                <button className='social-btn w-full mb-6' onClick={handleSignInWithMethod('google')}>
                  <img src='/icons8-google.svg' />

                  {t('auth.signup_google')}
                </button>
              </li>
              <li>
                <button className='social-btn w-full mb-6' onClick={handleSignInWithMethod('apple')}>
                  <img src='/apple.svg' />

                  {t('auth.signup_apple')}
                </button>
              </li>
              <li>
                <button className='social-btn w-full mb-6' onClick={handleSignInWithMethod('facebook')}>
                  <img src='/facebook.svg' />

                  {t('auth.signup_facebook')}
                </button>
              </li>
            </ul>

          </div>
        </>}
        {modalType === 'login' && <>
          <div className="flex items-start justify-between pt-16 pb-9 px-5  ">
            <h3 className="modal-title">
              {t('generic.welcome')}
            </h3>
            <button
              className="p-1 bg-transparent text-black text-3xl absolute top-2 right-4"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          {/*body*/}
          <div className="modal-box">

            <form onSubmit={handleSubmit}>
              <div className='mb-6'>
                <label className='block  mb-2'>
                  {t('generic.email')}*
                </label>
                <input type='text' className='border' name='email' autoComplete='email' value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className='mb-6'>
                <label className='block  mb-2'>
                  {t('auth.password_label')}*
                </label>
                <div className='flex gap-1'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className='border'
                    name='password'
                    autoComplete='password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className='flex justify-center items-center !border !border-gray-400 rounded-lg' onClick={togglePasswordVisibility}>
                    <button
                      type='button'
                      className='!bg-transparent h-full'
                    >
                      <img src={showPassword ? '/eye.svg' : '/eye-slash.svg'} className='w-6' />
                    </button>
                  </div>
                </div>
              </div>
              <div className='mt-6 mb-6'>
                <a className='link !underline !font-semibold' onClick={handlePromptResetPassword}>
                  {t('auth.forgot_password')}
                </a>
              </div>
              <button type='submit'
                className={`mid w-full btn-loading mb-6 ${loading ? 'running' : ''}`}
                disabled={loading}>
                <div className="loading"></div>
                {t('auth.connect')}
              </button>
            </form>

            <p>
              {t('auth.no_account')} <a className='link !underline !font-semibold' onClick={() => setModalType('signup')}> {t('auth.no_account_button')} </a>
            </p>
            <div className='separator-line'>
              <span>{t('generic.or')}</span>
            </div>
            <ul>
              <li>
                <button className='social-btn w-full mb-6' onClick={handleSignInWithMethod('google')}>
                  <img src='/icons8-google.svg' />
                  {t('auth.signup_google')}
                </button>
              </li>
              <li>
                <button className='social-btn w-full mb-6' onClick={handleSignInWithMethod('apple')}>
                  <img src='/apple.svg' />
                  {t('auth.signup_apple')}
                </button>
              </li>
              <li>
                <button className='social-btn w-full mb-6' onClick={handleSignInWithMethod('facebook')}>
                  <img src='/facebook.svg' />
                  {t('auth.signup_facebook')}
                </button>
              </li>
            </ul>

          </div>
        </>}
        {modalType === 'reset' && <>
          <div className="flex items-start justify-between pt-16 pb-9 px-5  ">
            <h3 className="modal-title">
              {t('auth.reset_password_title')}
            </h3>
            <button
              className="p-1 bg-transparent text-black text-3xl absolute top-2 right-4"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          {/*body*/}
          <div className="modal-box">

            <form onSubmit={handleSubmit}>
              <div className='mb-6'>
                <label className='block  mb-2'>
                  {t('generic.email')}*
                </label>
                <input type='text' className='border' name='email' autoComplete='email' />
              </div>
              <button type='submit'
                className={`mid w-full btn-loading ${loading ? 'running' : ''}`}
                disabled={loading}>
                <div className="loading"></div>
                {t('auth.reset_password_button')}
              </button>
            </form>
            <div className='mt-6'>
              <p>
                <a className='link !underline !font-semibold' onClick={() => setModalType('login')}>{t('auth.reset_password_return')}</a>
              </p>
            </div>
          </div>
        </>}
      </div>
      <div className="fixed inset-0 z-40 modal-bg auth-modal-bg" onClick={onClose}></div>
    </div>
  )
}

export default SignupModal
